// Global pull-to-refresh for mobile / tablet — desktop is untouched.
//
// Design notes:
// - Only activates on touch devices (`ontouchstart` in window) so mouse
//   wheels + desktop scrollbars behave normally.
// - The app uses per-page scroll containers (not window scroll —
//   <main> is `overflow-hidden`). So checking `window.scrollY` is
//   useless; we walk up from the touched element to find the first
//   scrollable ancestor and check *that* element's `scrollTop`. Pull
//   only engages when the scroll container is already at the top —
//   anywhere else on the page, the normal scroll gesture is preserved
//   and we never call preventDefault.
// - Threshold at 70px; spinner locks in once passed and the release
//   fires a full React Query invalidation (refetches every active
//   query on the current page).
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { tr } from "@/lib/i18n";

const THRESHOLD_PX = 70;
const MAX_PULL_PX = 140;
const DAMP = 0.55;

// Walk up from `el` and return the first ancestor whose vertical
// overflow is auto/scroll AND that actually has scrollable content.
// Falls back to document.scrollingElement (window scroll) if nothing
// matches — that still handles the "regular" case gracefully.
function findScrollableAncestor(el) {
  let node = el;
  while (node && node !== document.body && node !== document.documentElement) {
    const style = window.getComputedStyle(node);
    const oy = style.overflowY;
    // `auto` and `scroll` both scroll; skip `hidden`/`visible`. We
    // require scrollHeight > clientHeight so we don't pick containers
    // that just happen to be styled but aren't actually overflowing.
    if ((oy === "auto" || oy === "scroll") && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return document.scrollingElement || document.documentElement;
}

export function PullToRefresh({ children }) {
  const qc = useQueryClient();
  const [pull, setPull] = useState(0);       // current visible pull distance
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(null);               // touch start Y, null = inactive
  const scroller = useRef(null);             // the ancestor we're tracking
  const armed = useRef(false);               // becomes true once we cross threshold once

  useEffect(() => {
    // Feature-detect: skip entirely on non-touch (desktop with mouse).
    // `navigator.maxTouchPoints` catches iPads in desktop UA spoof mode.
    const isTouch =
      "ontouchstart" in window || (navigator.maxTouchPoints || 0) > 0;
    if (!isTouch) return undefined;

    const onTouchStart = (e) => {
      if (refreshing) return;
      // Find the ACTUAL scroll container under the finger. Because the
      // app nests scroll areas per-route inside overflow-hidden <main>,
      // we can't rely on window.scrollY at all.
      const target = (e.target instanceof Element) ? e.target : document.body;
      const sc = findScrollableAncestor(target);
      // Only arm the gesture when that container is already at the top.
      // Otherwise the user is mid-page and needs to scroll normally.
      if (sc.scrollTop > 0) {
        startY.current = null;
        scroller.current = null;
        return;
      }
      startY.current = e.touches[0].clientY;
      scroller.current = sc;
      armed.current = false;
    };

    const onTouchMove = (e) => {
      if (startY.current == null || refreshing) return;
      // If the tracked container has scrolled (e.g. content is
      // taller than viewport and user moved past top), abort — we
      // don't want to fight the browser's real scroll.
      const sc = scroller.current;
      if (sc && sc.scrollTop > 0) {
        startY.current = null;
        scroller.current = null;
        setPull(0);
        return;
      }
      const dy = e.touches[0].clientY - startY.current;
      if (dy <= 0) {
        // Upward swipe — user is scrolling down normally, disengage.
        setPull(0);
        return;
      }
      const damped = Math.min(dy * DAMP, MAX_PULL_PX);
      setPull(damped);
      if (damped >= THRESHOLD_PX) armed.current = true;
      // Prevent the browser's own overscroll bounce once we've clearly
      // taken over the gesture. Only after ~6px so tiny wobble doesn't
      // block real taps.
      if (damped > 6 && e.cancelable) e.preventDefault();
    };

    const onTouchEnd = async () => {
      if (startY.current == null) {
        // Wasn't tracking — nothing to reset.
        scroller.current = null;
        return;
      }
      const passed = armed.current;
      startY.current = null;
      scroller.current = null;
      armed.current = false;
      if (!passed) {
        setPull(0);
        return;
      }
      setRefreshing(true);
      // Snap the indicator to the threshold while refreshing.
      setPull(THRESHOLD_PX);
      try {
        // Refetch every active query on the current page. React Query
        // is our single source of truth for server data across the
        // app (Article view, Research runs, Library, Scheduled tasks,
        // Social comments, LLM cost, Settings, etc.) — one call
        // refreshes everything the user can actually see.
        await qc.invalidateQueries();
      } catch (err) {
        toast.error(tr("Refresh failed — try again"));
      } finally {
        setRefreshing(false);
        setPull(0);
      }
    };

    // `passive: false` on move so we can preventDefault the overscroll
    // bounce. start/end can stay passive for scroll perf.
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("touchcancel", onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [qc, refreshing]);

  const showing = pull > 0 || refreshing;
  // 1.0 → fully committed rotation for the arrow icon so the user
  // gets a visual "release to refresh" cue before letting go.
  const commit = Math.min(pull / THRESHOLD_PX, 1);

  return (
    <>
      {showing && (
        <div
          data-testid="pull-to-refresh-indicator"
          aria-hidden="true"
          className="fixed left-0 right-0 top-0 z-[100] flex justify-center pointer-events-none"
          style={{
            transform: `translateY(${Math.max(pull - 40, 0)}px)`,
            transition: refreshing ? "transform 150ms ease-out" : "none",
          }}
        >
          <div className="mt-2 h-10 w-10 grid place-items-center bg-white border border-[var(--border)] shadow-sm rounded-full">
            {refreshing ? (
              <Loader2 className="w-4 h-4 animate-spin text-[var(--accent)]" />
            ) : (
              <ArrowDown
                className={
                  "w-4 h-4 transition-transform duration-150 " +
                  (commit >= 1 ? "text-[var(--accent)]" : "text-[var(--ink-muted)]")
                }
                style={{
                  transform: `rotate(${commit >= 1 ? 180 : 0}deg)`,
                }}
              />
            )}
          </div>
        </div>
      )}
      {children}
    </>
  );
}

export default PullToRefresh;
